import { useEffect } from 'react'
import { AccountSettingsPanel } from './AccountSettingsPanel'
import { AppearanceSettingsPanel } from './AppearanceSettingsPanel'
import { FirestoreDebug } from './FirestoreDebug'
import { GroqSettingsPanel } from './GroqSettingsPanel'
import { LibrarySettingsPanel } from './LibrarySettingsPanel'
import { RecommendationSettingsPanel } from './RecommendationSettingsPanel'
import { TMDBSettingsPanel } from './TMDBSettingsPanel'
import { useUserSettings } from './useUserSettings'

interface Props {
  open: boolean
  onClose: () => void
}

/**
 * PWA settings sheet. Ports the desktop Settings view at 1:1 fidelity for
 * every setting that applies in a browser (desktop-only features like file
 * scanning, torrents, native video players, and OpenSubtitles are excluded).
 *
 * The layout is a right-anchored overlay on wide viewports and a full-screen
 * sheet on phones. All panels save on change via useWriteSettings — there's
 * no explicit save button. API-key panels include debounced (300ms) TMDB /
 * Groq validation with a status pill.
 */
export function SettingsSheet({ open, onClose }: Props) {
  const { settings, loading } = useUserSettings()

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-lg flex-col overflow-hidden bg-bg-primary shadow-2xl sm:max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border-primary bg-bg-primary px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-text-primary">
              Settings
            </h2>
            <p className="text-[11px] text-text-tertiary">
              Synced with your Govu account
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-bg-tertiary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-secondary"
          >
            Close
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-4">
            {loading && (
              <p className="rounded-md border border-border-primary bg-bg-secondary p-3 text-center text-[11px] text-text-tertiary">
                Loading settings from Firestore…
              </p>
            )}

            {/* Get Started — API keys first so new users can't miss
                them. TMDB is required for any metadata at all; Groq
                unlocks AI-assisted search. Free tier for both. */}
            <div className="rounded-lg border border-accent-primary/40 bg-accent-primary/5 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary text-sm font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-text-primary mb-1">Get Started</h3>
                  <p className="text-[11px] text-text-secondary leading-relaxed">
                    Paste your API keys below to unlock metadata and AI search. Grab free keys from{' '}
                    <span className="font-mono text-accent-primary">themoviedb.org</span> and{' '}
                    <span className="font-mono text-accent-primary">console.groq.com</span>. TMDB is required; Groq is optional.
                  </p>
                </div>
              </div>
            </div>
            <TMDBSettingsPanel settings={settings} />
            <GroqSettingsPanel settings={settings} />

            <AppearanceSettingsPanel settings={settings} />
            <LibrarySettingsPanel settings={settings} />
            <RecommendationSettingsPanel settings={settings} />
            <AccountSettingsPanel />

            <details className="rounded-lg border border-border-primary bg-bg-secondary p-3">
              <summary className="cursor-pointer text-xs font-semibold text-text-secondary">
                Firestore debug
              </summary>
              <div className="mt-3">
                <FirestoreDebug />
              </div>
            </details>

            <div className="pt-2 text-center text-[10px] text-text-tertiary">
              Govu PWA · v0.1.0
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
